// CT customization companion (see snippets/video.liquid): defer autoplay
// videos until they approach the viewport. Horizon's <deferred-media> keeps
// the real <video> inert inside a <template>; DeferredMedia#loadContent
// (assets/media.js) clones it in and force-plays autoplay videos. We trigger
// that from an IntersectionObserver instead of rendering the video eagerly,
// so a below-fold or next-slide gallery video costs zero bytes until a
// shopper is about to see it.
//
// Known tradeoff: loadContent dispatches mediaStartedPlaying, which pauses
// other deferred-media instances — with several autoplay videos near one
// viewport, only the most recently loaded keeps playing. Current pages have
// at most one; revisit if that changes.

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

if (!REDUCED_MOTION && !('IntersectionObserver' in window)) {
  // No IO (ancient in-app shells): load every marked video immediately —
  // exactly the old eager behavior, nothing lost.
  customElements.whenDefined('deferred-media').then(() => {
    for (const el of document.querySelectorAll('deferred-media[data-autoplay-in-view]')) el.loadContent?.(false);
  });
} else if (!REDUCED_MOTION) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        entry.target.loadContent?.(false);
      }
    },
    // One viewport of vertical lookahead: playback is already underway by the
    // time the shopper scrolls to the video. The 5% threshold stops
    // edge-touching carousel neighbours (x exactly at the viewport edge
    // counts as intersecting at threshold 0) from loading at page load —
    // a swipe reveals 5% within its first frames, so loading still starts
    // at the beginning of the gesture.
    { rootMargin: '100% 0px', threshold: 0.05 }
  );

  const observe = () => {
    for (const el of document.querySelectorAll('deferred-media[data-autoplay-in-view]:not([data-in-view-observed])')) {
      el.setAttribute('data-in-view-observed', '');
      io.observe(el);
    }
  };

  // Wait for the component upgrade so loadContent exists when IO fires.
  customElements.whenDefined('deferred-media').then(() => {
    observe();
    // Galleries swap media in and out (variant changes, carousels).
    new MutationObserver(observe).observe(document.body, { childList: true, subtree: true });
  });
}
// With reduced motion the poster + play button (restored via CSS) is the
// correct experience — no autoplay, video loads only on request.
